class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.isQuoteMode = window.storeMode === 'quote';

    // In quote mode, render from localStorage on load
    if (this.isQuoteMode) {
      this.renderQuoteContents();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (this.isQuoteMode) {
      this.renderQuoteContents();
      this.open();
      return;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  renderQuoteContents() {
    if (typeof QuoteCart === 'undefined') return;

    const items = QuoteCart.getItems();
    const cartItemsContainer = document.getElementById('CartDrawer-CartItems');
    const isEmpty = items.length === 0;

    this.classList.toggle('is-empty', isEmpty);
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', isEmpty);
    const emptyState = document.getElementById('CartDrawer-EmptyMessage');
    if (emptyState) emptyState.style.display = isEmpty ? '' : 'none';

    // Update footer subtotal
    const subtotalValue = this.querySelector('.totals__subtotal-value');
    if (subtotalValue) subtotalValue.style.display = 'none';
    const subtotalLabel = this.querySelector('.totals__subtotal');
    if (subtotalLabel) subtotalLabel.style.display = 'none';

    // Keep "Offerte aanvragen" always clickable
    const checkoutBtn = document.getElementById('CartDrawer-Checkout');
    if (checkoutBtn) {
      checkoutBtn.removeAttribute('aria-disabled');
      checkoutBtn.style.pointerEvents = '';
      checkoutBtn.style.opacity = '';
    }

    if (!cartItemsContainer) return;

    if (isEmpty) {
      cartItemsContainer.innerHTML = '';
      return;
    }

    const iconRemoveSvg = `<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><g stroke="currentColor" fill="none" stroke-linecap="square"><path d="M2 2l10 10M12 2L2 12"></path></g></svg>`;

    let itemsHtml = '';
    items.forEach((item) => {
      const imgSrc = item.image ? item.image.replace(/width=\d+/, 'width=100') : '';
      const imgHtml = imgSrc
        ? `<img src="${imgSrc}" alt="${item.productTitle}" loading="lazy" class="quote-drawer-item__image">`
        : '';

      const variantInfo = item.variantTitle && item.variantTitle !== 'Default Title'
        ? ` - ${item.variantTitle}`
        : '';

      let priceHtml = '';
      if (item.price && item.price > 0) {
        const euros = Math.floor(item.price / 100);
        const cents = item.price % 100;
        const formatted = cents === 0 ? `\u20AC${euros},-` : `\u20AC${euros},${String(cents).padStart(2, '0')}`;
        priceHtml = `<div class="quote-drawer-item__price">Vanaf ${formatted}</div>`;
      }

      itemsHtml += `
        <div class="quote-drawer-item">
          <a href="${item.url}" class="quote-drawer-item__media">${imgHtml}</a>
          <div class="quote-drawer-item__info">
            <a href="${item.url}" class="quote-drawer-item__title break">${item.productTitle}${variantInfo}</a>
            ${priceHtml}
          </div>
          <button type="button" data-quote-remove="${item.variantId}" aria-label="Verwijder" class="quote-drawer-item__remove">
            ${iconRemoveSvg}
          </button>
        </div>`;
    });

    cartItemsContainer.innerHTML = `<div class="quote-drawer-items">${itemsHtml}</div>`;

    // Bind remove buttons
    this.querySelectorAll('[data-quote-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        QuoteCart.removeItem(btn.dataset.quoteRemove);
        this.renderQuoteContents();
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
